import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { getCustomerById } from '@/modules/customer/api/customer.api'
import { Button } from '@/shared/components/ui/button'
import { syncAllAdminData } from '@/shared/lib/crmSync'
import {
  getBackendFieldErrors,
  parseZod,
  scrollToFirstError,
  toNumberOrUndefined,
} from '@/shared/lib/formValidation'
import { toast } from '@/shared/lib/toast'

import { getLeadById as getAdminLeadById } from '../../admin/api/leads.api'
import {
  createAdminQuotation,
  getAdminQuotationById,
  getAdminQuotationByLeadId,
  getNextAdminQuoteNumber,
  updateAdminQuotation,
} from '../../admin/api/quotation.api'
import { getLeadById as getMarketingLeadById } from '../../marketing/api/leads.api'
import {
  createMarketingQuotation,
  getMarketingQuotationById,
  getMarketingQuotationByLeadId,
  getNextMarketingQuoteNumber,
  updateMarketingQuotation,
} from '../../marketing/api/quotation.api'
import {
  getNextQuoteNumber,
  getQuotationById,
  getQuotationByLeadId,
  updateQuotation,
} from '../api/quotation.api'
import { getLeadById as getSalesLeadById } from '../api/sales.api'
import BankDetailsCard from '../components/quotations/create-quotation/BankDetailsCard'
import CustomerDetailsCard from '../components/quotations/create-quotation/CustomerDetailsCard'
import ProductDetailsCard from '../components/quotations/create-quotation/ProductDetailsCard'
import QuotationActions from '../components/quotations/create-quotation/QuotationActions'
import QuotationDetailsCard from '../components/quotations/create-quotation/QuotationDetailsCard'
import QuotationFooter from '../components/quotations/create-quotation/QuotationFooter'
import QuotationSummaryCard from '../components/quotations/create-quotation/QuotationSummaryCard'
import TermsConditionsCard from '../components/quotations/create-quotation/TermsConditionsCard'
import { useQuotation } from '../hooks/useQuotation'
import { createQuotationFormSchema } from '../schemas/quotation.schema'

function getLocalDateTimeString(dateInput = new Date()) {
  const d = new Date(dateInput)

  if (isNaN(d.getTime())) return ''

  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

function getInitialQuoteNo() {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()

  const startYear = month >= 3 ? year : year - 1
  const endYear = startYear + 1

  const fy = `${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`

  return `SDPL/${fy}/QUOTE/2014`
}

function getInitialForm(lead, hasExistingQuotation = false) {
  const defaultQuoteNo = getInitialQuoteNo()
  const defaultQuoteDate = getLocalDateTimeString()

  const contactName = lead?.contactPersonName || lead?.name || ''
  const accountName = lead?.companyName || lead?.company || ''
  const line1 = lead?.addressLine1 || lead?.completeAddress || ''
  const line2 = lead?.addressLine2 || ''
  const city = lead?.city || ''
  const state = lead?.state || ''
  const pincode = lead?.pincode || ''
  const country = lead?.country || ''

  const form = {
    taxType: 'cgst_sgst',

    quoteNo: defaultQuoteNo,
    contactName,
    accountName,
    quoteDate: defaultQuoteDate,
    validUntil: '',

    billingAddress: {
      line1,
      line2,
      city,
      state,
      pincode,
      country,
    },

    shippingAddress: {
      line1,
      line2,
      city,
      state,
      pincode,
      country,
    },

    termsAndConditions: {
      warranty: 'As per Manufacturer Warranty',
      paymentTerms: '30 Days from Invoice Date',
      deliveryTerms: 'Immediate',
      gstApplicability: '',
      additionalTerms: '',
    },

    products: [
      {
        product: '',
        description: '',
        hsn: '',
        unitPrice: '',
        quantity: 1,
        beforeGST: '',
        cgst: 9,
        sgst: 9,
        igst: 18,
        total: '',
        isFromLead: false,
      },
    ],
  }

  if (hasExistingQuotation === false && lead?.products?.length > 0) {
    form.products = lead.products.map((p) => {
      const unitPrice = Number(p.value ?? p.unitPrice) || 0
      const quantity = Number(p.quantity) || 1
      const beforeGST = unitPrice * quantity
      const gstAmount = (beforeGST * 18) / 100
      const total = beforeGST + gstAmount

      return {
        product: p.product || '',
        description: p.description || '',
        hsn: '',
        unitPrice: unitPrice ? String(unitPrice) : '',
        quantity,
        beforeGST: beforeGST ? beforeGST.toFixed(2) : '',
        cgst: 9,
        sgst: 9,
        igst: 18,
        total: total ? total.toFixed(2) : '',
        isFromLead: false,
      }
    })
  }

  return form
}

function getFormFromQuotation(quotation) {
  const taxType = quotation.taxType || 'cgst_sgst'

  return {
    taxType,

    quoteNo: quotation.quoteNo || '',
    contactName: quotation.contactName || '',
    accountName: quotation.accountName || '',

    quoteDate: quotation.quoteDate
      ? getLocalDateTimeString(quotation.quoteDate)
      : getLocalDateTimeString(),

    validUntil: quotation.validUntil
      ? new Date(quotation.validUntil).toISOString().slice(0, 10)
      : '',

    billingAddress: {
      line1: quotation.billingAddress?.line1 || '',
      line2: quotation.billingAddress?.line2 || '',
      city: quotation.billingAddress?.city || '',
      state: quotation.billingAddress?.state || '',
      pincode: quotation.billingAddress?.pincode || '',
      country: quotation.billingAddress?.country || '',
    },

    shippingAddress: {
      line1: quotation.shippingAddress?.line1 || '',
      line2: quotation.shippingAddress?.line2 || '',
      city: quotation.shippingAddress?.city || '',
      state: quotation.shippingAddress?.state || '',
      pincode: quotation.shippingAddress?.pincode || '',
      country: quotation.shippingAddress?.country || '',
    },

    termsAndConditions: {
      warranty:
        quotation.termsAndConditions?.warranty ||
        'As per Manufacturer Warranty',

      paymentTerms:
        quotation.termsAndConditions?.paymentTerms ||
        '30 Days from Invoice Date',

      deliveryTerms: quotation.termsAndConditions?.deliveryTerms || 'Immediate',

      gstApplicability:
        quotation.taxType === 'export_lut_igst' ||
        quotation.taxType === 'export_lut_igst_sgst_cgst'
          ? 'No tax applicable.'
          : '',

      additionalTerms: quotation.termsAndConditions?.additionalTerms || '',
    },

    products:
      quotation.products?.length > 0
        ? quotation.products.map((item) => {
            const unitPrice = Number(item.unitPrice ?? item.value) || 0
            const quantity = Number(item.quantity) || 1

            const cgst = item.cgst ?? 9
            const sgst = item.sgst ?? 9
            const igst = item.igst ?? 18

            const beforeGST = unitPrice * quantity

            const gstRate =
              taxType === 'igst'
                ? Number(igst) || 0
                : (Number(cgst) || 0) + (Number(sgst) || 0)

            const gstAmount = (beforeGST * gstRate) / 100

            const total = beforeGST + gstAmount

            return {
              product: item.product || '',
              description: item.description || '',
              quantity,
              hsn: item.hsn || '',
              unitPrice: unitPrice ? String(unitPrice) : '',
              beforeGST: beforeGST.toFixed(2),
              cgst,
              sgst,
              igst,
              total: total.toFixed(2),
              isFromLead: false,
            }
          })
        : [
            {
              product: '',
              description: '',
              hsn: '',
              unitPrice: '',
              quantity: 1,
              beforeGST: '',
              cgst: 9,
              sgst: 9,
              igst: 18,
              total: '',
              isFromLead: false,
            },
          ],
  }
}

export default function SalesCreateQuotationPage() {
  const navigate = useNavigate()
  const { leadId, quotationId } = useParams()
  const { state } = useLocation()
  const { user } = useAuth()

  const [lead, setLead] = useState(state?.lead || state?.customer || null)
  const isEditMode = Boolean(quotationId)
  const isLeadSelected =
    !isEditMode && Boolean(lead || (leadId && leadId !== 'new'))

  const [existingQuotation, setExistingQuotation] = useState(null)
  const [isLoadingQuotation, setIsLoadingQuotation] = useState(false)

  const { createQuotation } = useQuotation()

  const [sameAsBilling, setSameAsBilling] = useState(true)

  const [taxType, setTaxType] = useState('cgst_sgst')

  // Delivery Charge
  const [deliveryCharge, setDeliveryCharge] = useState('')
  const [showDeliveryCharge, setShowDeliveryCharge] = useState(false)

  const hasExistingQuoteInitially = Boolean(
    lead?.quotation?._id ||
    lead?.quotation?.quoteNo ||
    (lead?.quotation && typeof lead?.quotation === 'string') ||
    lead?.quotationStatus === 'sent' ||
    lead?.quotationStatus === 'created',
  )

  const [form, setForm] = useState(() =>
    getInitialForm(lead, hasExistingQuoteInitially),
  )

  const [errors, setErrors] = useState({})

  const isAdminPath = window.location.pathname.startsWith('/admin')

  const isMarketingPath = window.location.pathname.startsWith('/marketing')

  const backPath =
    state?.from === 'leads'
      ? isAdminPath
        ? '/admin/leads'
        : isMarketingPath
          ? '/marketing/leads'
          : '/sales/leads'
      : isEditMode
        ? isAdminPath
          ? '/admin/quotes'
          : isMarketingPath
            ? '/marketing/quotes'
            : '/sales/quotes'
        : isAdminPath
          ? '/admin/quotes'
          : isMarketingPath
            ? '/marketing/quotes'
            : '/sales/quotes'

  const fetchQuotation = isAdminPath
    ? getAdminQuotationById
    : isMarketingPath
      ? getMarketingQuotationById
      : getQuotationById

  const saveQuotation = isAdminPath
    ? updateAdminQuotation
    : isMarketingPath
      ? updateMarketingQuotation
      : updateQuotation

  /*
   * Fetch next auto-generated quote number when in create mode
   */
  useEffect(() => {
    if (isEditMode) return

    const fetchNextNumber = async () => {
      try {
        const apiCall = isAdminPath
          ? getNextAdminQuoteNumber
          : isMarketingPath
            ? getNextMarketingQuoteNumber
            : getNextQuoteNumber

        const res = await apiCall()

        if (res?.data?.quoteNo) {
          setForm((prev) => ({
            ...prev,
            quoteNo: res.data.quoteNo,
          }))
        }
      } catch (err) {
        console.error('Failed to fetch next quote number:', err)
      }
    }

    fetchNextNumber()
  }, [isEditMode, isAdminPath, isMarketingPath])

  /*
   * Load existing quotation when in edit mode
   */
  useEffect(() => {
    if (!isEditMode) return

    const loadQuotation = async () => {
      try {
        setIsLoadingQuotation(true)

        const response = await fetchQuotation(quotationId)

        const quotation = response?.data || response

        setExistingQuotation(quotation)

        setForm(getFormFromQuotation(quotation))

        setTaxType(quotation.taxType || 'cgst_sgst')

        // Load existing delivery charge if present
        const existingDeliveryCharge = Number(quotation.deliveryCharge) || 0

        if (existingDeliveryCharge > 0) {
          setDeliveryCharge(String(existingDeliveryCharge))
          setShowDeliveryCharge(true)
        } else {
          setDeliveryCharge('')
          setShowDeliveryCharge(false)
        }

        setSameAsBilling(false)
      } catch {
        toast.error('Failed to load quotation')
        navigate(backPath)
      } finally {
        setIsLoadingQuotation(false)
      }
    }

    loadQuotation()
  }, [quotationId, isEditMode, fetchQuotation, navigate, backPath])

  /*
   * Load lead/customer when in create mode
   */
  useEffect(() => {
    if (isEditMode) return

    const loadLeadAndCheckQuotation = async () => {
      let currentLead = lead

      if (leadId && leadId !== 'new') {
        try {
          if (leadId.startsWith('cust_')) {
            const customerId = leadId.replace('cust_', '')
            const res = await getCustomerById(customerId)
            if (res?.data) {
              currentLead = res.data
              setLead(currentLead)
            }
          } else {
            const fetchLeadFn = isAdminPath
              ? getAdminLeadById
              : isMarketingPath
                ? getMarketingLeadById
                : getSalesLeadById
            const res = await fetchLeadFn(leadId)
            if (res?.data) {
              currentLead = res.data
              setLead(currentLead)
            }
          }
        } catch (err) {
          console.error('Failed to load lead/customer:', err)
          toast.error('Failed to load details')
          return
        }
      }

      if (currentLead) {
        // Check for existing quotation
        try {
          const fetchQuotationFn = isAdminPath
            ? getAdminQuotationByLeadId
            : isMarketingPath
              ? getMarketingQuotationByLeadId
              : getQuotationByLeadId

          const idToUse =
            currentLead._id || currentLead.id || leadId?.replace('cust_', '')
          const quotationRes = await fetchQuotationFn(idToUse)

          const existingQuote = quotationRes?.data || quotationRes

          if (existingQuote && (existingQuote._id || existingQuote.quoteNo)) {
            // Existing quotation exists -> this is a second quotation creation for the same lead
            // Requirement 1: Keep products input box blank for second time
            setExistingQuotation(null)
            setForm((prev) => ({
              ...getInitialForm(currentLead, true),
              quoteNo: prev.quoteNo,
            }))
          } else {
            // First time quotation creation -> fetch products from lead (with updated descriptions)
            setExistingQuotation(null)
            setForm((prev) => ({
              ...getInitialForm(currentLead, false),
              quoteNo: prev.quoteNo,
            }))
          }
        } catch {
          // 404 means no existing quotation, which is expected for first time
          setExistingQuotation(null)
          setForm((prev) => ({
            ...getInitialForm(currentLead, false),
            quoteNo: prev.quoteNo,
          }))
        }
      }
    }

    loadLeadAndCheckQuotation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, isEditMode, isAdminPath, isMarketingPath])

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev

      const next = { ...prev }
      delete next[key]

      return next
    })
  }

  const handleChange = (field, value) => {
    clearError(field)

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleBillingChange = (fieldOrUpdates, value) => {
    setForm((prev) => {
      const updates =
        typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null
          ? fieldOrUpdates
          : { [fieldOrUpdates]: value }

      const billingAddress = {
        ...prev.billingAddress,
        ...updates,
      }

      return {
        ...prev,
        billingAddress,
        shippingAddress: sameAsBilling ? billingAddress : prev.shippingAddress,
      }
    })
  }

  const handleShippingChange = (fieldOrUpdates, value) => {
    setForm((prev) => {
      const updates =
        typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null
          ? fieldOrUpdates
          : { [fieldOrUpdates]: value }

      return {
        ...prev,
        shippingAddress: {
          ...prev.shippingAddress,
          ...updates,
        },
      }
    })
  }

  const handleTermsChange = (field, value) => {
    clearError(`termsAndConditions.${field}`)

    setForm((prev) => ({
      ...prev,
      termsAndConditions: {
        ...prev.termsAndConditions,
        [field]: value,
      },
    }))
  }

  const handleSameAsBilling = (checked) => {
    setSameAsBilling(checked)

    if (checked) {
      setForm((prev) => ({
        ...prev,
        shippingAddress: {
          ...prev.billingAddress,
        },
      }))
    }
  }

  const addProduct = () => {
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          product: '',
          description: '',
          hsn: '',
          unitPrice: '',
          quantity: 1,
          beforeGST: '',
          cgst: 9,
          sgst: 9,
          igst: 18,
          total: '',
          isFromLead: false,
        },
      ],
    }))
  }

  const removeProduct = (index) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }))
  }

  const handleProductChange = (index, field, value) => {
    clearError(`products.${index}.${field}`)

    clearError('products')

    setForm((prev) => {
      const products = [...prev.products]

      products[index] = {
        ...products[index],
        [field]: value,
      }

      const product = products[index]

      const unitPrice = Number(product.unitPrice) || 0

      const quantity = Number(product.quantity) || 0

      const cgst = Number(product.cgst) || 0

      const sgst = Number(product.sgst) || 0

      const igst = Number(product.igst) || 0

      const beforeGST = unitPrice * quantity

      const isExport =
        taxType === 'export_lut_igst' || taxType === 'export_lut_igst_sgst_cgst'

      const gstRate = isExport ? 0 : taxType === 'igst' ? igst : cgst + sgst

      const gstAmount = (beforeGST * gstRate) / 100

      const total = beforeGST + gstAmount

      products[index].beforeGST = beforeGST.toFixed(2)

      products[index].total = total.toFixed(2)

      return {
        ...prev,
        products,
      }
    })
  }

  /*
   * Change tax type between:
   * - CGST + SGST
   * - IGST
   *
   * Recalculate all product totals using the
   * selected tax type.
   */
  const handleTaxTypeChange = (value) => {
    setTaxType(value)

    const isExport =
      value === 'export_lut_igst' || value === 'export_lut_igst_sgst_cgst'

    const dynamicGstApplicability = isExport ? 'No tax applicable.' : ''

    setForm((prev) => ({
      ...prev,
      taxType: value,

      termsAndConditions: {
        ...prev.termsAndConditions,
        gstApplicability: dynamicGstApplicability,
      },

      products: prev.products.map((product) => {
        const unitPrice = Number(product.unitPrice) || 0

        const quantity = Number(product.quantity) || 0

        const beforeGST = unitPrice * quantity

        const nextCgst = value === 'cgst_sgst' ? (product.cgst ?? 9) : undefined

        const nextSgst = value === 'cgst_sgst' ? (product.sgst ?? 9) : undefined

        const nextIgst = value === 'igst' ? (product.igst ?? 18) : undefined

        const gstRate = isExport
          ? 0
          : value === 'igst'
            ? (nextIgst ?? 0)
            : (nextCgst ?? 0) + (nextSgst ?? 0)

        const gstAmount = (beforeGST * gstRate) / 100

        const total = beforeGST + gstAmount

        return {
          ...product,

          cgst: nextCgst,
          sgst: nextSgst,
          igst: nextIgst,

          beforeGST: beforeGST.toFixed(2),

          total: total.toFixed(2),
        }
      }),
    }))
  }

  /*
   * Delivery Charge
   */
  const handleAddDeliveryCharge = () => {
    setShowDeliveryCharge(true)
  }

  const handleDeliveryChargeChange = (value) => {
    setDeliveryCharge(value)
  }

  const handleRemoveDeliveryCharge = () => {
    setDeliveryCharge('')
    setShowDeliveryCharge(false)
  }

  /*
   * Existing product calculations remain unchanged.
   *
   * Delivery charge is added separately only to
   * the final Grand Total.
   */
  const subTotal = form.products.reduce(
    (sum, item) => sum + (Number(item.beforeGST) || 0),
    0,
  )

  const productGrandTotal = form.products.reduce(
    (sum, item) => sum + (Number(item.total) || 0),
    0,
  )

  const gstAmount = productGrandTotal - subTotal

  const deliveryChargeAmount = Number(deliveryCharge) || 0

  const grandTotal = productGrandTotal + deliveryChargeAmount

  const handleCancel = () => {
    navigate(backPath)
  }

  /*
   * SUBMIT — creates or updates depending on mode
   */
  const handleCreateQuotation = async () => {
    const formattedProducts = form.products.map((item) => {
      const unitPrice = toNumberOrUndefined(item.unitPrice)

      const quantity = toNumberOrUndefined(item.quantity)

      const cgst = toNumberOrUndefined(item.cgst)

      const sgst = toNumberOrUndefined(item.sgst)

      const igst = toNumberOrUndefined(item.igst)

      const beforeGST =
        unitPrice !== undefined && quantity !== undefined
          ? unitPrice * quantity
          : undefined

      const gstRate =
        taxType === 'igst'
          ? igst
          : cgst !== undefined && sgst !== undefined
            ? cgst + sgst
            : undefined

      const gstVal =
        beforeGST !== undefined && gstRate !== undefined
          ? (beforeGST * gstRate) / 100
          : undefined

      return {
        product: String(item.product || '').trim(),
        description: String(item.description || '').trim(),
        hsn: String(item.hsn || '').trim(),

        unitPrice,
        quantity,
        beforeGST,

        cgst: taxType === 'cgst_sgst' ? cgst : undefined,

        sgst: taxType === 'cgst_sgst' ? sgst : undefined,

        igst: taxType === 'igst' ? igst : undefined,

        total:
          beforeGST !== undefined && gstVal !== undefined
            ? beforeGST + gstVal
            : undefined,
      }
    })

    const payload = {
      lead: isEditMode
        ? existingQuotation?.lead?._id || existingQuotation?.lead || undefined
        : leadId?.startsWith('cust_')
          ? leadId
          : String(lead?._id || leadId || '').trim(),

      quoteNo: String(form.quoteNo || '').trim(),

      contactName: String(form.contactName || '').trim(),

      accountName: String(form.accountName || '').trim(),

      quoteDate: form.quoteDate || '',

      validUntil: form.validUntil
        ? new Date(form.validUntil).toISOString()
        : null,

      billingAddress: form.billingAddress,

      shippingAddress: form.shippingAddress,

      termsAndConditions: {
        ...form.termsAndConditions,
        gstApplicability:
          taxType === 'export_lut_igst' ||
          taxType === 'export_lut_igst_sgst_cgst'
            ? 'No tax applicable.'
            : '',
      },

      taxType,

      products: formattedProducts,

      subTotal,
      gstAmount,
      grandTotal,

      // Delivery charge is separate
      // and does not affect GST.
      deliveryCharge: deliveryChargeAmount,
    }

    const targetQuoteId = isEditMode ? quotationId : null

    if (targetQuoteId) {
      try {
        setErrors({})

        const quoteDate = new Date(payload.quoteDate).toISOString()

        await saveQuotation(targetQuoteId, {
          ...payload,
          quoteDate,
        })

        toast.success('Quotation updated successfully')

        syncAllAdminData({
          type: 'quotation:updated',
          quotationId: targetQuoteId,
        })

        navigate(backPath)
      } catch (error) {
        toast.error(error?.message || 'Failed to update quotation')

        setErrors(getBackendFieldErrors(error))
      }

      return
    }

    const parsed = parseZod(createQuotationFormSchema, payload)

    if (!parsed.success) {
      setErrors(parsed.errors)

      toast.error(parsed.message)

      scrollToFirstError()

      return
    }

    try {
      setErrors({})

      const quoteDate = new Date(parsed.data.quoteDate).toISOString()

      if (isAdminPath) {
        const res = await createAdminQuotation({
          ...parsed.data,
          quoteDate,
        })

        toast.success(res?.message || 'Quotation created successfully')
      } else if (isMarketingPath) {
        const res = await createMarketingQuotation({
          ...parsed.data,
          quoteDate,
        })

        toast.success(res?.message || 'Quotation created successfully')
      } else {
        await createQuotation({
          ...parsed.data,
          quoteDate,
        })
      }

      syncAllAdminData({ type: 'quotation:created' })

      navigate(backPath)
    } catch (error) {
      toast.error(error?.message || 'Failed to create quotation')

      setErrors(getBackendFieldErrors(error))
    }
  }

  if (isLoadingQuotation) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <Button
          variant="ghost"
          className="w-fit gap-2 px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
          onClick={() => navigate(backPath)}
        >
          <ArrowLeft className="h-4 w-4" />

          {isEditMode || existingQuotation?._id
            ? 'Back to Quotations'
            : 'Back to Leads'}
        </Button>

        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditMode || existingQuotation?._id
              ? 'Edit Quotation'
              : 'Create Quotation'}
          </h1>

          <p className="mt-1 text-sm text-muted-foreground">
            {isEditMode || existingQuotation?._id
              ? `Editing ${existingQuotation?.quoteNo || ''}`
              : 'Create a quotation for the selected lead.'}
          </p>
        </div>
      </div>

      {/* Quotation Details */}
      <QuotationDetailsCard
        form={form}
        errors={errors}
        onChange={handleChange}
        isLeadSelected={isLeadSelected}
      />

      {/* Customer Details */}
      <CustomerDetailsCard
        form={form}
        sameAsBilling={sameAsBilling}
        onSameAsBillingChange={handleSameAsBilling}
        onBillingChange={handleBillingChange}
        onShippingChange={handleShippingChange}
      />

      {/* Products + Tax */}
      <ProductDetailsCard
        products={form.products}
        errors={errors}
        onAddProduct={addProduct}
        onRemoveProduct={removeProduct}
        onProductChange={handleProductChange}
        taxType={taxType}
        onTaxTypeChange={handleTaxTypeChange}
        isLeadSelected={isLeadSelected}
        deliveryCharge={deliveryCharge}
        showDeliveryCharge={showDeliveryCharge}
        onAddDeliveryCharge={handleAddDeliveryCharge}
        onDeliveryChargeChange={handleDeliveryChargeChange}
        onRemoveDeliveryCharge={handleRemoveDeliveryCharge}
      />

      {/* Summary */}
      <QuotationSummaryCard
        subTotal={subTotal}
        gstAmount={gstAmount}
        deliveryCharge={deliveryChargeAmount}
        grandTotal={grandTotal}
      />

      {/* Terms & Conditions */}
      <TermsConditionsCard
        terms={form.termsAndConditions}
        errors={errors}
        onChange={handleTermsChange}
        taxType={taxType}
      />

      {/* Bank Details */}
      <BankDetailsCard />

      {/* Footer */}
      <QuotationFooter
        generatedBy={
          existingQuotation?.createdBy?.name ||
          (isAdminPath ? user?.name : lead?.salesPerson?.name || user?.name) ||
          ''
        }
      />

      {/* Actions */}
      <QuotationActions
        onCancel={handleCancel}
        onCreate={handleCreateQuotation}
        isEditMode={isEditMode}
      />
    </div>
  )
}
